import { Container, Graphics, Matrix, Point, Rectangle, Sprite, Texture } from "pixi.js"
import { logger } from "../../../../logger"
import { degrees_to_radians } from "game-common/util"
import { Coordinate, randomIntBetween } from "game-common/models"
import { getDeathEffectStyle } from "./death_effect_config"
import { findRendererApp } from "../renderer_app"

// Shared slice-and-animate core behind every sprite's entity-death `fragment()`.
//
// Each sprite class builds + positions a `surface` Graphics and resolves the
// current animation frame `frame`, then hands both here. This slices the frame
// into a grid of little sub-sprites and animates them with whichever death
// style is currently active (see death_effect_config): the original radial
// "explode", or the new gravity-driven "crumble".
//
// The `onSpawned` hook fires once after all fragments are created (BasicSprite
// uses it to resolve its fragment callback).

const randomFloatBetween = (min: number, max: number): number => Math.random() * (max - min) + min

// Alpha byte above which a pixel counts as "solid" when locating the visible
// bottom (the feet) of a death frame. See computeContentBottomPx.
const CONTENT_ALPHA_THRESHOLD = 8

// Memoized foot line (local-pixel bottom of the visible art), keyed by a stable
// art identifier -- race for creatures, spriteId for basic sprites. The foot
// line within a cell is a static property of a species' art (outfit/action barely
// move it), so a whole species costs ONE readback for the session, then zero.
// Stores a plain number, so it's immune to the destroyed-texture staleness that
// bites texture caches here (no HMR clearing needed).
const footLineCache: Map<string, number> = new Map()

interface ShatterOpts {
    onSpawned?: () => void
    // Stable art identifier used to cache the crumble foot line across deaths.
    cacheKey?: string
    // Where crumble pieces come to rest, in frame pixels: the line the body stands on. A body that
    // knows its own ground passes it; without it, the art's visible bottom is read back instead.
    ground?: number
}

export const shatterSprite = (surface: Graphics, frame: Texture, opts: ShatterOpts = {}): void => {
    const style = getDeathEffectStyle()
    const { x: origX, y: origY, width, height } = frame._frame

    const divisor = height < 50 || width < 50 ? 3 : randomIntBetween(10, 20)
    const cols = Math.floor(width / divisor)
    const rows = Math.floor(height / divisor)

    // Fragments (and, for crumble, dust motes) each drive their own timer. We
    // remove the shared `surface` only once the last one has finished.
    let awaitedCount = 0
    const cleaner = () => {
        if (awaitedCount < 1) {
            surface.parent?.removeChild(surface)
        }
    }
    const onFinished = () => {
        awaitedCount--
        cleaner()
    }

    // For crumble, locate the visible bottom (feet) of the art so every piece
    // settles at the creature's real foot line rather than the (often
    // padding-filled) bottom of the sprite-sheet cell. Cached per art identifier
    // (opts.cacheKey), so it's one GPU readback per species, then free.
    const crumbleGround = style !== "crumble" ? 0 : opts.ground ?? resolveCrumbleGround(frame, rows, divisor, opts.cacheKey)

    for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
            const fragX = origX + x * divisor
            const fragY = origY + y * divisor

            const frag = new Texture(
                frame.baseTexture,
                new Rectangle(fragX, fragY, divisor, divisor),
                undefined,
                undefined,
                frame.rotate,
                frame.defaultAnchor,
            )

            const fragSprite = new Sprite(frag)
            fragSprite.x = x * divisor
            fragSprite.y = y * divisor
            surface.addChild(fragSprite)

            awaitedCount++
            if (style === "crumble") {
                animateCrumbleFragment(fragSprite, { row: y, rows, divisor }, crumbleGround, onFinished)
            } else {
                animateExplodeFragment(fragSprite, onFinished)
            }
        }
    }

    const fragmentCount = awaitedCount

    // A puff of dust rising off the base sells the crumble.
    if (style === "crumble" && fragmentCount > 0) {
        const dustCount = 6
        for (let d = 0; d < dustCount; d++) {
            awaitedCount++
            spawnDustMote(surface, width, crumbleGround, onFinished)
        }
    }

    // Matches the original loop's semantics: fire only once fragments exist.
    if (fragmentCount > 0) {
        opts.onSpawned?.()
    } else {
        // Nothing to animate (frame smaller than the divisor): tear down now.
        surface.parent?.removeChild(surface)
    }
}

/**
 * A surface in `host`'s space laid exactly over the frame `shown` is drawing: its origin on the
 * frame's top-left pixel, one unit per frame pixel, turned as the body is turned. shatterSprite cuts
 * its pieces in frame pixels from that origin, so they start over the art they were cut from.
 *
 * Taken from the drawn sprite's own transform rather than from the body's bounds: those take in all
 * that hangs off a body (its cast shadow, its rings, its speech), none of it centred on the art, and
 * a shatter placed by them jumped as it began.
 */
export const surfaceOverFrame = (shown: Sprite, host: Container): Graphics => {
    // Brings both world transforms up to date, in case the body moved since it was last drawn.
    host.toLocal(new Point(0, 0), shown)
    const { orig, trim } = shown.texture
    const topLeft = new Matrix(1, 0, 0, 1, (trim?.x ?? 0) - shown.anchor.x * orig.width,
        (trim?.y ?? 0) - shown.anchor.y * orig.height)
    const surface = new Graphics()
    surface.transform.setFromMatrix(host.worldTransform.clone().invert().append(shown.worldTransform).append(topLeft))
    host.addChild(surface)
    return surface
}

// --- Original "explode": radial constant-velocity scatter, spin, red tint, fade over ~500ms. ---
const animateExplodeFragment = (fragSprite: Sprite, onFinished: () => void): void => {
    const angle = degrees_to_radians(randomIntBetween(0, 360))
    const speed2: Coordinate = { x: Math.cos(angle), y: Math.sin(angle) }

    let rotation = 0
    const startTs = Date.now()
    let changedColor1 = false
    let changedColor2 = false

    const interval = setInterval(() => {
        fragSprite.x += speed2.x * 10
        fragSprite.y += speed2.y * 10
        fragSprite.alpha -= 0.1

        rotation += 1
        if (rotation > 360) {
            rotation = 0
        }
        fragSprite.angle = rotation

        if (fragSprite.alpha < 0.7 && !changedColor1) {
            fragSprite.tint = 0xffc0c0
            changedColor1 = true
        }
        if (fragSprite.alpha < 0.5 && !changedColor2) {
            fragSprite.tint = 0xff0000
            changedColor2 = true
        }

        if (Date.now() - startTs > 500) {
            removeFrag(fragSprite)
            clearInterval(interval)
            onFinished()
        }
    }, 50)
}

// --- New "crumble": pieces fall under gravity, tumble, bounce on the ground, settle then fade. ---
const animateCrumbleFragment = (
    fragSprite: Sprite,
    grid: { row: number; rows: number; divisor: number },
    groundLine: number,
    onFinished: () => void,
): void => {
    const gravity = 1.4
    const bounce = 0.32

    // Tumbles about its middle, and is followed by its middle: a piece followed by its top-left corner
    // came to rest with its TOP on the ground, the whole pile hanging a piece's height below the feet.
    const half = grid.divisor / 2
    fragSprite.anchor.set(0.5)
    fragSprite.x += half
    fragSprite.y += half

    // Rests on the foot line by its bottom edge, with a little jitter for an uneven pile. Never above
    // the piece's own start, so a fragment already at/below the foot settles in place instead of rising.
    const ground = Math.max(fragSprite.y, groundLine - half + randomFloatBetween(-grid.divisor * 0.4, grid.divisor * 0.25))
    // Lower rows give way first, so the figure collapses from the base upward.
    const delay = (grid.rows - 1 - grid.row) * 18

    let vx = randomFloatBetween(-2.2, 2.2)
    let vy = randomFloatBetween(-1.5, 0.5)
    let spin = randomFloatBetween(-4, 4)
    let settled = false
    let tinted = false

    const startTs = Date.now()
    const startAt = startTs + delay

    const interval = setInterval(() => {
        const now = Date.now()
        if (now < startAt) {
            return
        }

        if (!settled) {
            vy += gravity
            fragSprite.y += vy
            fragSprite.x += vx
            fragSprite.angle += spin

            if (fragSprite.y >= ground && vy > 0) {
                fragSprite.y = ground
                vy *= -bounce
                vx *= 0.55
                spin *= 0.5
                if (Math.abs(vy) < 1) {
                    vy = 0
                    settled = true
                }
            }
        }

        // Once it has come to rest (or lingered long enough) it fades to dust.
        if (settled || now - startAt > 700) {
            fragSprite.alpha -= 0.06
            if (fragSprite.alpha < 0.6 && !tinted) {
                fragSprite.tint = 0x9a8f80 // dusty grey-brown, not fiery red
                tinted = true
            }
        }

        if (fragSprite.alpha <= 0 || now - startTs > 1600) {
            removeFrag(fragSprite)
            clearInterval(interval)
            onFinished()
        }
    }, 50)
}

// Resolve the local-pixel y where crumble pieces should pile: the bottom of the
// frame's visible art (the feet), falling back to the bottom of the sprite-sheet
// cell when a pixel read isn't possible. When a cacheKey is given, the resolved
// foot line is memoized per art identifier (see footLineCache) so we do at most
// one readback per species. A failed/unavailable read is NOT cached, so we retry
// on the next death (e.g. if the renderer wasn't ready the first time).
const resolveCrumbleGround = (frame: Texture, rows: number, divisor: number, cacheKey?: string): number => {
    if (cacheKey) {
        const cached = footLineCache.get(cacheKey)
        if (cached !== undefined) {
            return cached
        }
    }
    // Cache miss (or no key given): pay for one pixel readback.
    const contentBottom = computeContentBottomPx(frame)
    if (contentBottom != null && contentBottom > 0) {
        if (cacheKey) {
            footLineCache.set(cacheKey, contentBottom)
        }
        return contentBottom
    }
    return rows * divisor
}

// Read back the frame's pixels and return the local-pixel y of the lowest row
// that still has a solid (non-transparent) pixel -- the visible bottom of the
// art. Returns null if the renderer/extract plugin is unavailable (e.g. tests)
// or the frame is empty. Resolution-independent: works off canvas-height fractions.
const computeContentBottomPx = (frame: Texture): number | null => {
    const extract = (findRendererApp()?.renderer as any)?.plugins?.extract
    if (!extract) {
        return null
    }
    const probe = new Sprite(frame)
    try {
        const canvas: HTMLCanvasElement = extract.canvas(probe)
        const ctx = canvas.getContext("2d")
        const cw = canvas.width
        const ch = canvas.height
        if (!ctx || cw === 0 || ch === 0) {
            return null
        }
        const data = ctx.getImageData(0, 0, cw, ch).data
        const frameHeight = frame._frame.height
        for (let y = ch - 1; y >= 0; y--) {
            const rowOffset = y * cw * 4
            for (let x = 0; x < cw; x++) {
                if (data[rowOffset + x * 4 + 3] > CONTENT_ALPHA_THRESHOLD) {
                    // Map the canvas row back into the frame's local pixel space.
                    return ((y + 1) / ch) * frameHeight
                }
            }
        }
        return null
    } catch (e) {
        logger.error({ tags: ["sprite", "error"] }, e)
        return null
    } finally {
        probe.destroy()
    }
}

// A single dust mote off the shared WHITE texture (batches cheaply): rises, drifts, shrinks, fades.
const spawnDustMote = (surface: Graphics, width: number, baseY: number, onFinished: () => void): void => {
    const dust = new Sprite(Texture.WHITE)
    dust.tint = 0x8b7355
    const size = randomIntBetween(2, 4)
    dust.width = size
    dust.height = size
    dust.x = randomIntBetween(0, Math.max(1, width))
    dust.y = randomIntBetween(Math.floor(baseY * 0.7), Math.max(1, Math.floor(baseY)))
    dust.alpha = 0.6
    surface.addChild(dust)

    const vx = randomFloatBetween(-0.8, 0.8)
    const vy = randomFloatBetween(-3.5, -2)

    const interval = setInterval(() => {
        dust.x += vx
        dust.y += vy
        dust.alpha -= 0.03
        dust.scale.set(dust.scale.x * 0.97)

        if (dust.alpha <= 0) {
            removeFrag(dust)
            clearInterval(interval)
            onFinished()
        }
    }, 50)
}

const removeFrag = (sprite: Sprite): void => {
    try {
        if (sprite?.parent) {
            sprite.parent.removeChild(sprite)
        }
    } catch (e) {
        logger.error({ tags: ["sprite", "error"] }, e)
    }
}
